import { LOGIN_PATH } from '@/constants/routes';
import { ReactNode, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

interface FrontchannelLogoutMessage {
  type: 'frontchannel_logout';
  redirectTo?: string;
  timestamp?: number;
  messageId: string;
}

interface FrontchannelLogoutAck {
  type: 'frontchannel_logout_ack';
  messageId: string;
}

const isFrontchannelLogoutMessage = (
  message: unknown
): message is FrontchannelLogoutMessage => {
  return (
    typeof message === 'object' &&
    message !== null &&
    'type' in message &&
    (message as FrontchannelLogoutMessage).type === 'frontchannel_logout' &&
    'messageId' in message &&
    typeof (message as FrontchannelLogoutMessage).messageId === 'string'
  );
};

export const FrontchannelLogoutEffect = ({
  children,
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handledIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let broadcastChannel: BroadcastChannel | null = null;

    const sendAck = (
      ack: FrontchannelLogoutAck,
      targetOrigin?: string,
      targetWindow?: WindowProxy | null,
      bc?: BroadcastChannel | null
    ) => {
      try {
        targetWindow?.postMessage(ack, targetOrigin ?? '*');
      } catch {
        // 親ウィンドウへの応答失敗は無視する
      }

      try {
        bc?.postMessage(ack);
      } catch {
        // BroadcastChannel への応答失敗は無視する
      }

      try {
        localStorage.setItem('frontchannel_logout_ack', JSON.stringify(ack));
        setTimeout(() => {
          try {
            localStorage.removeItem('frontchannel_logout_ack');
          } catch {
            // localStorage 片付け失敗は無視する
          }
        }, 100);
      } catch {
        // localStorage 書き込み失敗は無視する
      }
    };

    const handleLogout = (message: FrontchannelLogoutMessage) => {
      const { redirectTo, timestamp, messageId } = message;

      if (handledIdsRef.current.has(messageId)) return;
      handledIdsRef.current.add(messageId);

      const timeDiff = timestamp ? Date.now() - timestamp : 0;
      if (timestamp && timeDiff >= 5 * 60 * 1000) return;

      const targetPath = redirectTo || LOGIN_PATH;
      const finalPath = location.search
        ? `${targetPath}${location.search}`
        : targetPath;

      navigate(finalPath, { replace: true });
    };

    const createAck = (messageId: string): FrontchannelLogoutAck => ({
      type: 'frontchannel_logout_ack',
      messageId,
    });

    const handlePostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isFrontchannelLogoutMessage(event.data)) return;

      sendAck(
        createAck(event.data.messageId),
        event.origin,
        event.source as WindowProxy | null,
        broadcastChannel
      );
      handleLogout(event.data);
    };

    try {
      broadcastChannel = new BroadcastChannel('frontchannel-logout');
      broadcastChannel.onmessage = (event) => {
        if (!isFrontchannelLogoutMessage(event.data)) return;

        sendAck(
          createAck(event.data.messageId),
          undefined,
          null,
          broadcastChannel
        );
        handleLogout(event.data);
      };
    } catch (error) {
      console.error('BroadcastChannel not supported:', error);
    }

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== 'frontchannel_logout_trigger' || !event.newValue)
        return;

      try {
        const message = JSON.parse(event.newValue);
        if (!isFrontchannelLogoutMessage(message)) return;

        sendAck(
          createAck(message.messageId),
          undefined,
          null,
          broadcastChannel
        );
        handleLogout(message);
      } catch (error) {
        console.error('Failed to parse storage event:', error);
      }
    };

    window.addEventListener('message', handlePostMessage);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('message', handlePostMessage);
      window.removeEventListener('storage', handleStorageEvent);
      broadcastChannel?.close();
    };
  }, [location.search, navigate]);

  return <>{children}</>;
};
